import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import { ParentingPlan } from "../models/ParentingPlan";
import { Comment } from "../models/Comment";
import { ParentingPlanService } from "../services/ParentingPlanService";
import { useAuth } from "./AuthContext";
import { useMatter } from "./MatterContext";
import { MatterService } from "../services/MatterService";
import { useSocketEventByIds } from "../hooks/useSocketEventByIds";

interface Errors {
  list?: Error | null;
  details?: Error | null;
}
interface Loadings {
  list?: boolean;
  details?: boolean;
}

interface ParentingPlanContextProps {
  loading: Loadings;
  error: Errors;
  currentParentingPlan: ParentingPlan | undefined;
  parentingPlan: ParentingPlan | null;
  parentingPlans: ParentingPlan[];
  comments: Comment[];
  createParentingPlan: (
    parentingPlanData: Omit<
      ParentingPlan,
      | "_id"
      | "createdAt"
      | "updatedAt"
      | "updatedById"
      | "createdById"
      | "matterId"
    >,
  ) => Promise<string>;
  updateParentingPlan: (
    parentingPlanId: string,
    parentingPlanData: Partial<
      Omit<ParentingPlan, "_id" | "createdAt" | "updatedAt">
    >,
  ) => Promise<void>;
  deleteParentingPlanById: (parentingPlanId: string) => Promise<void>;
  setSelectedParentingPlan: (parentingPlanId: string) => void;
}

const ParentingPlanContext = createContext<
  ParentingPlanContextProps | undefined
>(undefined);

export const useParentingPlan = (): ParentingPlanContextProps => {
  const context = useContext(ParentingPlanContext);
  if (!context) {
    throw new Error(
      "useParentingPlan must be used within a ParentingPlanProvider",
    );
  }
  return context;
};

export const ParentingPlanProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const { currentMatterId } = useMatter();
  const { user } = useAuth();

  const [parentingPlans, setParentingPlans] = useState<ParentingPlan[]>([]);
  const [parentingPlan, setParentingPlan] = useState<ParentingPlan | null>(
    null,
  );
  const [comments, setComments] = useState<Comment[]>([]);
  const [loading, setLoading] = useState<Loadings>({});
  const [error, setError] = useState<Errors>({});

  const currentParentingPlan = parentingPlans?.find(
    (o) => o.status === "active",
  );

  const fetchParentingPlans = async () => {
    if (!user || !currentMatterId) return;
    try {
      const docs = await MatterService.getParentingPlans(currentMatterId);
      setParentingPlans(docs);
      setError((prev) => ({ ...prev, list: null }));
    } catch (err: any) {
      setError((prev) => ({ ...prev, list: err }));
    } finally {
      setLoading((prev) => ({ ...prev, list: false }));
    }
  };

  const fetchComments = async (parentingPlanId: string) => {
    try {
      const commentList =
        await ParentingPlanService.getComments(parentingPlanId);
      setComments(commentList);
    } catch (err) {
      console.error("Failed to fetch parentingPlan comments:", err);
    }
  };

  useEffect(() => {
    setLoading((prev) => ({ ...prev, list: true }));
    fetchParentingPlans();
  }, [user, currentMatterId]);

  useSocketEventByIds("parentingplans:changed", ["*"], () => {
    fetchParentingPlans();
  });

  const setSelectedParentingPlan = (parentingPlanId: string) => {
    const selected =
      parentingPlans.find((d) => d._id === parentingPlanId) || null;
    setParentingPlan(selected);
    if (selected) fetchComments(selected._id);
  };

  const createParentingPlan = async (
    parentingPlanData: Omit<
      ParentingPlan,
      | "_id"
      | "createdAt"
      | "updatedAt"
      | "updatedById"
      | "createdById"
      | "matterId"
    >,
  ): Promise<string> => {
    if (!currentMatterId) return "";
    const newId = await ParentingPlanService.createParentingPlan({
      ...parentingPlanData,
      matterId: currentMatterId,
    });
    return newId;
  };

  const updateParentingPlan = async (
    parentingPlanId: string,
    updatedData: Partial<
      Omit<ParentingPlan, "_id" | "createdAt" | "updatedAt">
    >,
  ) => {
    await ParentingPlanService.updateParentingPlan(parentingPlanId, {
      ...updatedData,
      matterId: currentMatterId!,
    });
  };

  const deleteParentingPlanById = async (parentingPlanId: string) => {
    if (!currentMatterId) return;
    await ParentingPlanService.deleteParentingPlan(
      currentMatterId,
      parentingPlanId,
    );
    fetchParentingPlans();
  };

  return (
    <ParentingPlanContext.Provider
      value={{
        loading,
        error,
        currentParentingPlan,
        parentingPlan,
        parentingPlans,
        comments,
        createParentingPlan,
        updateParentingPlan,
        deleteParentingPlanById,
        setSelectedParentingPlan,
      }}
    >
      {children}
    </ParentingPlanContext.Provider>
  );
};
